/**
 * View for the reader review session. Shows a summary of the previous section of text to help the
 * user who may not be sure they understood it.
 */

import { effect } from "@preact/signals";
import $ from "jquery";
import * as preact from "preact";

import * as analytics from "analytics";
import * as premiumUpsellModal from "premiumUpsellModal";
import * as readerColorScheme from "readerColorScheme";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import * as subscriptionPlans from "shared/subscriptionPlans";
import BookStore from "stores/BookStore";
import stores from "stores/stores";

interface Props {
  bookID: string;
  position: number;
  appRouter: any;
  returnPosition: number;
}

interface State {
  summary?: string;
  badResponse?: boolean;
  secondsSinceFetching: number;
  reachedLimit?: boolean;
}

class ReaderReviewSessionView extends preact.Component<Props, State> {
  /**
   * Timeout ID for the loading timer. This allows us to cancel the timeout upon unmounting the
   * component.
   */
  loadingTimeoutId: number | undefined;
  private disposeReviewSummaryEffect?: () => void;
  private disposeGuideStateEffect?: () => void;

  constructor(props: Props) {
    super(props);

    this.state = { secondsSinceFetching: 0 };

    // The guide (needed for the plot-summary link) is normally already loaded
    // by the reader page; fetch it here only on a direct load of this page
    if (stores.guide.state.value.bookID !== props.bookID) {
      void stores.guide.loadForBook(props.bookID);
    }

    // Fetch review summary data from server
    this.fetchSession();

    this.runLoadingTimer();

    analytics.trackEvent("readerReviewOpen");

    if (readerColorScheme.get() === "colorDark") {
      $("body").addClass("colorDark");
    }
  }

  componentDidMount() {
    let firstRun = true;
    this.disposeReviewSummaryEffect = effect(() => {
      void stores.user.state.value.reviewSummaryInEnglish;
      if (firstRun) {
        firstRun = false;
        return;
      }
      void this.onUserChange();
    });

    // Re-render when the guide finishes loading so the plot-summary link can
    // appear (signal auto-subscription isn't reliable for class components)
    let firstGuideRun = true;
    this.disposeGuideStateEffect = effect(() => {
      void stores.guide.state.value;
      if (firstGuideRun) {
        firstGuideRun = false;
        return;
      }
      this.forceUpdate();
    });
  }

  onUserChange = async () => {
    if (
      this.state.summary === undefined &&
      this.state.badResponse === undefined
    ) {
      // We're already fetching the summary, so no need to re-fetch
      return;
    }

    await this.setState({
      summary: undefined,
      secondsSinceFetching: 0,
      badResponse: undefined,
    });
    this.fetchSession();
  };

  componentWillUnmount() {
    this.disposeReviewSummaryEffect?.();
    this.disposeGuideStateEffect?.();

    if (this.loadingTimeoutId !== undefined) {
      clearTimeout(this.loadingTimeoutId);
    }
  }

  /** This increments `secondsSinceFetching` every second while we are waiting for the response */
  async runLoadingTimer() {
    this.loadingTimeoutId = setTimeout(() => {
      this.setState({
        secondsSinceFetching: this.state.secondsSinceFetching + 1,
      });

      // Call recursively while we're still waiting for the challenges to load
      if (this.state.summary === undefined && !this.state.badResponse) {
        this.runLoadingTimer();
      }
    }, 1000);
  }

  async fetchSession(forceRegenerate = false) {
    try {
      if (stores.bookModel?.id !== this.props.bookID) {
        stores.bookModel = new BookStore({ _id: this.props.bookID });
        await stores.bookModel.fetch();
      }
    } catch (error) {
      // Failure loading the book (fetchSession is fired un-awaited from the
      // constructor): show "Generating Failed" with a retry instead of
      // spinning on "Generating summary..." forever
      console.error("Error loading book for review session:", error);
      this.setState({ badResponse: true });
      return;
    }

    try {
      const language = stores.bookModel.book.value.language;
      const { data } = await readlangClient.request(
        "POST",
        "/api/readerReview/generateSummary",
        {
          language: stores.bookModel.book.value.language,
          // No `text`: the server derives the passage from `bookID` and
          // `position` itself, so the shared summary cache can't be keyed on one
          // passage and filled with a summary of another
          bookID: this.props.bookID,
          position: this.props.position,
          forceRegenerate,
          inEnglish:
            language !== "en" && stores.user.isReviewSummaryInEnglish(language),
        }
      );

      this.setState({
        summary: data.summary,
      });
    } catch (error) {
      if (error.jqXHR?.status === 403) {
        this.setState({ reachedLimit: true });
        return;
      }
      if (error.jqXHR?.alreadyNotified) {
        // Terminal failure (network loss, 5xx): readlangClient already
        // notified — offer a manual retry instead of immediately triggering
        // another expensive regeneration.
        this.setState({ badResponse: true });
        return;
      }
      if (forceRegenerate) {
        this.setState({ badResponse: true });
        analytics.trackEvent("readerReviewLoadingFailed");
        // Throw error so that we have rollbar logs
        throw error;
      } else {
        // Try again with forceRegenerate=true
        this.fetchSession(true);
      }
    }
  }

  renderCloseButton() {
    return (
      <div
        class="closeButton"
        onClick={() => {
          this.returnToReader();
          analytics.trackEvent("readerReviewClose");
        }}
      >
        <i class="icon icon-remove" />
      </div>
    );
  }

  render() {
    if (this.state.reachedLimit) {
      return (
        <div class="readerReview">
          {this.renderCloseButton()}
          <h2>Review limit reached</h2>
          <p>
            You can only generate one review summary per day on the free plan.
            Please{" "}
            <a
              href="#"
              onClick={() => {
                event.preventDefault();
                event.stopPropagation();
                if (stores.user.hasPremiumAccess.value) {
                  alert(
                    "You've reached the daily limit of " +
                      subscriptionPlans.permissions.standard.reviewSummaries +
                      " review summaries per day intended to " +
                      "prevent abuse of the system. Contact steve@readlang.com if you think this " +
                      "has happened in error."
                  );
                }
                this.upgrade();
              }}
            >
              upgrade to premium
            </a>{" "}
            for unlimited access.
          </p>
        </div>
      );
    }

    if (this.state.badResponse) {
      return (
        <div class="readerReview">
          {this.renderCloseButton()}
          <h2>Generating failed</h2>
          <p>There was a problem generating your review session.</p>
          <p>
            <button
              onClick={() => {
                this.setState({ badResponse: false });
                this.fetchSession(true);
              }}
            >
              Try Again
            </button>
          </p>
        </div>
      );
    }

    if (this.state.summary === undefined) {
      return (
        <div class="readerReview">
          {this.renderCloseButton()}
          {
            [
              <p>Generating summary...</p>,
              <p>...shouldn't take too long...</p>,
              <p>...just another few seconds...</p>,
            ][Math.floor(this.state.secondsSinceFetching / 4) % 3]
          }
        </div>
      );
    }

    const language = stores.bookModel.book.value.language;
    const inEnglish =
      language !== "en" && stores.user.isReviewSummaryInEnglish(language);

    return (
      <div class="readerReview">
        {this.renderCloseButton()}
        <div>
          <h2>What's going on?</h2>
          <p class="summarySubheading">
            A short summary of the last section, generated by AI
          </p>
          <p
            class="summaryText"
            {...(languageMap.map[language]?.direction === "RTL" && !inEnglish
              ? { dir: "rtl" }
              : {})}
          >
            {this.state.summary}
          </p>
          {language !== "en" ? (
            <p>
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();

                  stores.user.setReviewSummaryToEnglish(language, !inEnglish);
                }}
              >
                Switch summary to{" "}
                {inEnglish ? languageMap.map[language].english : "English"}
              </a>
            </p>
          ) : null}
          {this.renderGuideLink()}
        </div>
        <div class="footer">
          <button
            class={`primary payButton`}
            onClick={() => {
              this.returnToReader();
            }}
          >
            Return to text
          </button>
        </div>
      </div>
    );
  }

  /**
   * Link to the book's full plot summary, shown only when a Reading Guide
   * with plot sections already exists for this book. Clicking returns to the
   * reader with the Guide tab opening on arrival.
   */
  renderGuideLink() {
    const guideState = stores.guide.state.value;
    if (
      guideState.status !== "ready" ||
      guideState.bookID !== this.props.bookID ||
      !guideState.guide?.sections.length
    ) {
      return null;
    }
    return (
      <p>
        <a
          href="#"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();

            analytics.trackEvent("readerReviewOpenGuide");
            stores.guide.requestOpenTab();
            this.returnToReader();
          }}
        >
          See the full plot summary in your Reading Guide
        </a>
      </p>
    );
  }

  upgrade() {
    premiumUpsellModal.show({
      title: "reviewSummariesLimit",
      heading: "Upgrade for more than 1 review summary per day",
      subheading:
        "Sanity check your understanding with unlimited AI-based review summaries and more!",
      showTitle: false,
      smartDefinitions: true,
    });
  }

  returnToReader() {
    this.props.appRouter.navigate(
      `/library/${this.props.bookID}/from/${this.props.returnPosition}`,
      {
        trigger: true,
      }
    );
  }
}

export default ReaderReviewSessionView;
